/**
 * Landing Page view component
 * Main entry point for the Furniturr application showcasing features and benefits
 */

// #region Imports
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import LandingHeroOrganism from "@/components/organisms/LandingHero/landing-hero.organism";
import LandingFeaturesOrganism from "@/components/organisms/LandingFeatures/landing-features.organism";
import LandingBenefitsOrganism from "@/components/organisms/LandingBenefits/landing-benefits.organism";
import LandingProcessOrganism from "@/components/organisms/LandingProcess/landing-process.organism";
import LandingTargetAudienceOrganism from "@/components/organisms/LandingTargetAudience/landing-target-audience.organism";
import LandingCTAOrganism from "@/components/organisms/LandingCTA/landing-cta.organism";
import LandingFooterOrganism from "@/components/organisms/LandingFooter/landing-footer.organism";
// #endregion

// #region Types
/**
 * Props for LandingPage view component
 */
interface LandingPageViewProps {
    /** Optional test ID for testing purposes */
    "data-testid"?: string;
}
// #endregion

// #region Component
/**
 * LandingPage view component displaying the complete landing experience
 * Showcases the Furniturr app's purpose, features, and value proposition
 *
 * @param props - The component props
 * @returns JSX element representing the landing page
 */
function LandingPageView(props: LandingPageViewProps) {
    const { t } = useTranslation("common");

    return (
        <Box
            component="main"
            sx={{
                backgroundColor: "background.default",
                minHeight: "100vh",
            }}
            {...props}
        >
            {/* Skip Navigation Link */}
            <Box
                component="a"
                href="#main-content"
                sx={{
                    position: "absolute",
                    left: "-9999px",
                    top: "16px",
                    zIndex: 1000,
                    padding: "8px 16px",
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    textDecoration: "none",
                    borderRadius: 1,
                    "&:focus": {
                        left: "16px",
                    },
                }}
            >
                {t("navigation.skipToMain")}
            </Box>

            {/* Hero Section */}
            <Box id="hero">
                <LandingHeroOrganism />
            </Box>

            {/* Main Content */}
            <Box id="main-content">
                {/* Features Section */}
                <Box id="features">
                    <LandingFeaturesOrganism />
                </Box>

                {/* How It Works Process Section */}
                <Box id="how-it-works">
                    <LandingProcessOrganism />
                </Box>

                {/* Benefits Section */}
                <Box id="benefits">
                    <LandingBenefitsOrganism />
                </Box>

                {/* Target Audience Section */}
                <Box id="target-audience">
                    <LandingTargetAudienceOrganism />
                </Box>

                {/* Call to Action Section */}
                <Box id="get-started">
                    <LandingCTAOrganism />
                </Box>
            </Box>

            {/* Footer Section */}
            <Box id="footer">
                <LandingFooterOrganism />
            </Box>
        </Box>
    );
}
// #endregion

// #region Exports
export default LandingPageView;
export type { LandingPageViewProps };
// #endregion
